'use client';

import Link from 'next/link';
import { KingsyardLogoMark } from '@/components/KingsyardLogo';
import { useEffect, useState } from 'react';

export default function ResourceHubPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 8) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFaq = index => {
    setOpenFaqIndex(openFaqIndex === index ? -1 : index);
  };

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Free Printable Birding Templates & Resources',
    url: 'https://www.kingsyard.com/resources',
    description:
      'Free printable birding templates from Kingsyard: birding journals, backyard bird bingo, feeder care logs and more.',
    hasPart: [
      {
        '@type': 'CreativeWork',
        name: 'Printable Birding Journal',
        url: 'https://www.kingsyard.com/resources/birding-journal',
        description: 'Track species, behaviors and observations across three printable field journal pages.',
      },
      {
        '@type': 'CreativeWork',
        name: 'Printable Backyard Bird Bingo',
        url: 'https://www.kingsyard.com/resources/backyard-bird-bingo',
        description: 'Three bingo card variants with 24 backyard birds each. Race family and friends to five in a row.',
      },
      {
        '@type': 'CreativeWork',
        name: 'Printable Feeder Care Log',
        url: 'https://www.kingsyard.com/resources/feeder-care-log',
        description: 'Keep every feeder clean, filled and safe with a simple monthly care log.',
      },
    ],
  };

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Free Printable Birding Templates',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Printable Birding Journal',
        url: 'https://www.kingsyard.com/resources/birding-journal',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Printable Backyard Bird Bingo',
        url: 'https://www.kingsyard.com/resources/backyard-bird-bingo',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Printable Feeder Care Log',
        url: 'https://www.kingsyard.com/resources/feeder-care-log',
      },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Are these birding templates really free?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Every template in the Resource Hub is free to use, print and share. No signup and no email required.',
        },
      },
      {
        '@type': 'Question',
        name: 'What paper size do the templates use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'All templates are laid out for US Letter paper (8.5 x 11 in). They also print well on A4 with the standard fit to page setting.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I fill in the templates on screen?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Open a template and fill in the fields right in your browser, then hit Print This Page to print your completed version. You can also print blank pages first and fill them in by hand outdoors.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use these templates in my classroom or birding club?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. Teachers, scout leaders and birding clubs are welcome to print as many copies as they need for their group.',
        },
      },
      {
        '@type': 'Question',
        name: 'Will you add more printable templates?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. New templates are added every season, from bird count sheets to seasonal checklists. Bookmark this page and check back.',
        },
      },
    ],
  };

  return (
    <>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }}
      />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
      />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <link
        rel='preconnect'
        href='https://fonts.googleapis.com'
      />
      <link
        rel='preconnect'
        href='https://fonts.gstatic.com'
        crossOrigin='anonymous'
      />
      <link
        href='https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Open+Sans:wght@400;600;700&display=swap'
        rel='stylesheet'
      />

      <style
        dangerouslySetInnerHTML={{
          __html: `
        :root {
          --green: #2f6132;
          --green-dark: #1e4320;
          --green-light: #a8d5aa;
          --green-bg: #f0f5f0;
          --cream: #f7f4ef;
          --border: #ede9e2;
          --text-dark: #1a2e1b;
          --text-mid: #444444;
          --text-muted: #666666;
          --gold: #d4a017;
          --max-width: 1160px;
          --radius: 12px;
          --shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
          --shadow-hover: 0 6px 32px rgba(47, 97, 50, 0.15);
          --white: #ffffff;
          --charcoal: #1a1a1a;
          --gray-border: #e8e6e2;
        }

        .resource-page-root *,
        .resource-page-root *::before,
        .resource-page-root *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        .resource-page-root em {
          font-style: normal;
        }

        .resource-page-root {
          font-family:
            'Open Sans',
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            Roboto,
            sans-serif;
          font-size: 16px;
          line-height: 1.7;
          color: var(--text-mid);
          background: var(--white);
          -webkit-font-smoothing: antialiased;
          width: 100%;
          /* 这里不能用 overflow-x: hidden：它会把 overflow-y 计算成 auto，
             使本元素变成滚动容器，内部 .site-header 的 position: sticky 随之失效。
             横向溢出由各 section 自己的 overflow: hidden 兜住（如 .hero 的装饰图形）。 */
        }

        .resource-page-root h1,
        .resource-page-root h2,
        .resource-page-root h3,
        .resource-page-root h4 {
          font-family: 'Poppins', 'Open Sans', sans-serif;
          color: var(--text-dark);
          line-height: 1.2;
        }
        .resource-page-root img {
          max-width: 100%;
          display: block;
        }
        .resource-page-root a {
          color: var(--green);
        }

        .resource-page-root .container {
          width: 100%;
          max-width: var(--max-width);
          margin: 0 auto;
          padding: 0 20px;
        }

        /* TOP BANNER */
        .resource-page-root .top-banner {
          background: var(--green);
          color: #fff;
          font-family: 'Poppins', sans-serif;
          text-align: center;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.3px;
        }

        /* SITE HEADER */
        .resource-page-root .site-header {
          position: sticky;
          top: 0;
          z-index: 100;
          background: var(--white);
          border-bottom: 1px solid var(--gray-border);
          transition: box-shadow 0.3s;
        }
        .resource-page-root .site-header.scrolled {
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }
        .resource-page-root .header-inner {
          width: 100%;
          padding: 0 32px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .resource-page-root .header-logo {
          font-size: 20px;
          font-weight: 800;
          color: var(--charcoal);
          text-decoration: none;
          letter-spacing: 2px;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .resource-page-root .header-logo:hover {
          color: var(--green);
        }
        .resource-page-root .header-logo svg {
          height: 40px;
          width: auto;
          display: block;
        }
        .resource-page-root .header-nav {
          display: flex;
          gap: 32px;
          align-items: center;
          flex-wrap: nowrap;
          min-width: 0;
        }
        .resource-page-root .header-nav a {
          white-space: nowrap;
        }
        .resource-page-root .header-nav a {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: var(--charcoal);
          text-decoration: none;
          transition: color 0.2s;
          letter-spacing: 1px;
        }
        .resource-page-root .header-nav a:hover,
        .resource-page-root .header-nav a.current {
          color: var(--green);
        }
        .resource-page-root .header-cta {
          display: inline-block;
          background: var(--green);
          color: var(--white);
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          padding: 8px 24px;
          border-radius: 6px;
          text-decoration: none;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .resource-page-root .header-cta:hover {
          background: #245027;
        }
        .resource-page-root .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .resource-page-root .mobile-menu-btn svg {
          width: 24px;
          height: 24px;
          stroke: var(--charcoal);
          fill: none;
          stroke-width: 2;
        }
        .resource-page-root .mobile-menu {
          display: none;
          position: fixed;
          top: 64px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-border);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 99;
          padding: 16px;
        }
        .resource-page-root .mobile-menu.active {
          display: block;
        }
        .resource-page-root .mobile-menu a {
          display: block;
          padding: 12px 0;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: var(--charcoal);
          text-decoration: none;
          border-bottom: 1px solid var(--gray-border);
          letter-spacing: 0.5px;
        }
        .resource-page-root .mobile-menu a:hover,
        .resource-page-root .mobile-menu a.current {
          color: var(--green);
        }
        .resource-page-root .mobile-cta {
          display: block;
          margin-top: 16px;
          background: var(--green);
          color: var(--white);
          text-align: center;
          padding: 14px 24px;
          border-radius: 6px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          border-bottom: none;
        }

        /* HERO */
        .resource-page-root .hero {
          background: linear-gradient(135deg, var(--green-dark) 0%, var(--green) 50%, #3d7540 100%);
          color: #fff;
          text-align: center;
          padding: 64px 20px 88px;
          position: relative;
          overflow: hidden;
        }
        .resource-page-root .hero-deco {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }
        .resource-page-root .hero-deco svg {
          position: absolute;
          opacity: 0.07;
        }
        .resource-page-root .hero-deco .hb-1 {
          width: 230px;
          top: -36px;
          right: -28px;
          transform: rotate(-8deg);
        }
        .resource-page-root .hero-deco .hb-2 {
          width: 130px;
          bottom: 46px;
          left: -14px;
          transform: rotate(7deg);
        }
        .resource-page-root .hero-deco .hb-3 {
          width: 92px;
          top: 42%;
          right: 10%;
          transform: rotate(-13deg);
          opacity: 0.05;
        }
        .resource-page-root .hero-badge,
        .resource-page-root .hero h1,
        .resource-page-root .hero-sub,
        .resource-page-root .hero-badges {
          position: relative;
          z-index: 1;
        }
        .resource-page-root .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--green-light);
          color: var(--green-dark);
          font-family: 'Poppins', sans-serif;
          font-size: 11px;
          font-weight: 700;
          padding: 6px 18px;
          border-radius: 40px;
          margin-bottom: 20px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .resource-page-root .hero h1 {
          font-size: clamp(30px, 5vw, 50px);
          font-weight: 800;
          color: #fff;
          max-width: 820px;
          margin: 0 auto 16px;
        }
        .resource-page-root .hero h1 em {
          color: var(--green-light);
        }
        .resource-page-root .hero-sub {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.85);
          max-width: 560px;
          margin: 0 auto 28px;
          line-height: 1.6;
        }
        .resource-page-root .hero-badges {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 20px;
        }
        .resource-page-root .hero-badges span {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Poppins', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: var(--green-light);
          letter-spacing: 0.04em;
        }
        .resource-page-root .hero-badges svg {
          width: 16px;
          height: 16px;
          stroke: var(--green-light);
          fill: none;
          stroke-width: 2.2;
        }

        /* SECTIONS */
        .resource-page-root .section {
          padding: 80px 0 64px;
        }
        .resource-page-root .section-gray {
          background: var(--cream);
        }
        .resource-page-root #templates {
          background: #fff;
          border-radius: 24px 24px 0 0;
          margin-top: -32px;
          position: relative;
          z-index: 2;
        }
        .resource-page-root .section-label {
          display: block;
          font-family: 'Poppins', sans-serif;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          color: var(--green);
          margin-bottom: 12px;
        }
        .resource-page-root .section-head {
          max-width: 640px;
          margin-bottom: 40px;
        }
        .resource-page-root .section-head.center {
          margin-left: auto;
          margin-right: auto;
          text-align: center;
        }
        .resource-page-root .section-head h2 {
          font-size: clamp(26px, 3.4vw, 36px);
          font-weight: 800;
          margin-bottom: 12px;
        }
        .resource-page-root .section-head h2 em {
          color: var(--green);
        }
        .resource-page-root .section-head p {
          font-size: 17px;
          color: var(--text-muted);
        }

        /* 纯静态 Grid 布局，完美平铺，绝不引发任何首帧错位 */
        .resource-page-root .tpl-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 24px;
          align-items: stretch;
          width: 100%;
        }
        .resource-page-root .tpl-card {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow);
          display: flex;
          flex-direction: column;
          height: 100%;
          min-width: 0;
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }
        .resource-page-root .tpl-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-hover);
        }
        .resource-page-root .tpl-card .card-img {
          aspect-ratio: 17 / 22;
          background: var(--cream);
          overflow: hidden;
          border-bottom: 1px solid var(--border);
          padding: 14px;
        }
        .resource-page-root .tpl-card .card-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
          border: 1px solid var(--border);
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          transition: transform 0.3s ease;
        }
        .resource-page-root .tpl-card:hover .card-img img {
          transform: scale(1.03);
        }
        .resource-page-root .tpl-card .card-body {
          padding: 22px 22px 24px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .resource-page-root .tpl-card .card-cat {
          font-family: 'Poppins', sans-serif;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--green);
          margin-bottom: 8px;
        }
        .resource-page-root .tpl-card h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 8px;
          line-height: 1.35;
        }
        .resource-page-root .tpl-card h2 a {
          color: var(--text-dark);
          text-decoration: none;
        }
        .resource-page-root .tpl-card h2 a:hover {
          color: var(--green);
        }
        .resource-page-root .tpl-card .card-desc {
          font-size: 15px;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 14px;
        }
        .resource-page-root .tpl-card .card-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 18px;
        }
        .resource-page-root .tpl-card .card-meta span {
          font-family: 'Poppins', sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: var(--green);
          background: var(--green-bg);
          padding: 4px 10px;
          border-radius: 4px;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .resource-page-root .tpl-card .card-cta {
          margin-top: auto;
          display: block;
          background: var(--green);
          color: #fff;
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          text-align: center;
          text-decoration: none;
          padding: 14px 16px;
          border-radius: 10px;
          transition:
            background 0.2s,
            transform 0.2s;
        }
        .resource-page-root .tpl-card .card-cta:hover {
          background: var(--green-dark);
          transform: translateY(-1px);
          color: #fff;
        }
        .resource-page-root .grid-hint {
          margin-top: 28px;
          text-align: center;
          font-size: 15px;
          color: var(--text-muted);
        }

        /* HOW TO USE */
        .resource-page-root .howto-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
          align-items: stretch;
        }
        .resource-page-root .howto-card {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 28px;
          display: flex;
          gap: 18px;
          align-items: flex-start;
          height: 100%;
          min-width: 0;
        }
        .resource-page-root .howto-num {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          background: var(--green);
          color: #fff;
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 700;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .resource-page-root .howto-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .resource-page-root .howto-card p {
          font-size: 15px;
          color: var(--text-muted);
        }

        /* FAQ */
        .resource-page-root .faq-wrap {
          max-width: 760px;
          margin: 0 auto;
        }
        .resource-page-root .faq-item {
          border: 1px solid var(--border);
          border-radius: var(--radius);
          background: #fff;
          margin-bottom: 12px;
          overflow: hidden;
        }
        .resource-page-root .faq-item button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 24px;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: var(--text-dark);
          text-align: left;
        }
        .resource-page-root .faq-item .faq-icon {
          flex-shrink: 0;
          font-size: 22px;
          line-height: 1;
          color: var(--green);
          transition: transform 0.28s ease;
        }
        .resource-page-root .faq-item.open .faq-icon {
          transform: rotate(45deg);
        }
        .resource-page-root .faq-item .faq-answer {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.28s ease;
          contain: layout paint;
        }
        .resource-page-root .faq-item.open .faq-answer {
          grid-template-rows: 1fr;
        }
        .resource-page-root .faq-item .faq-answer-inner {
          overflow: hidden;
          min-height: 0;
          font-size: 15px;
          line-height: 1.8;
          color: var(--text-mid);
        }
        .resource-page-root .faq-item .faq-answer-inner > p {
          padding: 0 24px 20px;
        }

        @media (prefers-reduced-motion: reduce) {
          .resource-page-root .faq-item .faq-answer,
          .resource-page-root .faq-item .faq-icon {
            transition: none;
          }
        }

        /* USE CASES */
        .resource-page-root .use-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
          align-items: stretch;
          width: 100%;
        }
        .resource-page-root .use-card {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 26px 24px;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-width: 0;
        }
        .resource-page-root .use-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .resource-page-root .use-card p {
          font-size: 15px;
          color: var(--text-muted);
          margin-bottom: 12px;
          flex: 1;
        }
        .resource-page-root .use-card a {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 700;
          color: var(--green);
          text-decoration: none;
          margin-top: auto;
        }
        .resource-page-root .use-card a:hover {
          text-decoration: underline;
        }

        /* BOTTOM CTA */
        .resource-page-root .final-cta {
          text-align: center;
          padding: 96px 24px;
          background: var(--green);
          color: #fff;
        }
        .resource-page-root .final-cta h2 {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(28px, 4vw, 40px);
          font-weight: 800;
          margin-bottom: 16px;
          color: #fff;
        }
        .resource-page-root .final-cta h2 em {
          color: var(--green-light);
        }
        .resource-page-root .final-cta .sub {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 40px;
          max-width: 560px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }
        .resource-page-root .final-cta .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #fff;
          color: var(--green);
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          padding: 16px 40px;
          border-radius: 10px;
          text-decoration: none;
          transition: all 0.25s;
        }
        .resource-page-root .final-cta .cta-btn:hover {
          background: #f6f5f3;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }
        .resource-page-root .final-cta .cta-btn svg {
          width: 18px;
          height: 18px;
        }

        /* FOOTER */
        .resource-page-root footer {
          background: #0d2410;
          color: rgba(255, 255, 255, 0.7);
          padding: 24px 32px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .resource-page-root footer .footer-copyright {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: rgba(255, 255, 255, 0.5);
        }
        .resource-page-root footer .footer-links {
          display: flex;
          list-style: none;
          gap: 20px;
          margin: 0;
          padding: 0;
        }
        .resource-page-root footer .footer-links a {
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: rgba(255, 255, 255, 0.4);
          text-decoration: none;
          transition: color 0.2s;
        }
        .resource-page-root footer .footer-links a:hover {
          color: var(--green-light);
        }

        /* RESPONSIVE */
        /* 中等宽度只收间距，字号与字距保持设计稿的 14px / 1px */
        @media (max-width: 1120px) {
          .resource-page-root .header-inner {
            padding: 0 20px;
          }
          .resource-page-root .header-nav {
            gap: 22px;
          }
          .resource-page-root .header-cta {
            padding: 8px 18px;
          }
        }

        @media (max-width: 1024px) {
          .resource-page-root .tpl-grid,
          .resource-page-root .use-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .resource-page-root .header-inner {
            padding: 0 16px;
          }
          .resource-page-root .header-nav {
            gap: 16px;
          }
          .resource-page-root .header-cta {
            padding: 8px 14px;
          }
          .resource-page-root .header-logo svg {
            height: 34px;
          }
        }

        @media (max-width: 900px) {
          .resource-page-root .header-nav,
          .resource-page-root .header-cta {
            display: none;
          }
          .resource-page-root .mobile-menu-btn {
            display: block;
            position: absolute;
            right: 16px;
          }
          .resource-page-root .header-inner {
            padding: 0 16px;
            justify-content: center;
            position: relative;
          }
          .resource-page-root .header-logo {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }
          .resource-page-root .tpl-grid,
          .resource-page-root .use-grid,
          .resource-page-root .howto-grid {
            grid-template-columns: minmax(0, 1fr);
          }
          .resource-page-root .section {
            padding: 56px 0 44px;
          }
          .resource-page-root .final-cta {
            padding: 64px 20px;
          }
          .resource-page-root footer {
            flex-direction: column;
            text-align: center;
            padding: 24px 16px;
          }
          .resource-page-root footer .footer-links {
            flex-wrap: wrap;
            justify-content: center;
            gap: 12px 16px;
          }
        }

        @media (max-width: 639px) {
          .resource-page-root .hero {
            padding: 44px 16px 56px;
          }
          .resource-page-root .hero-badges {
            gap: 10px 18px;
          }
          .resource-page-root .container {
            padding: 0 16px;
          }
          .resource-page-root .tpl-card .card-body {
            padding: 18px 18px 20px;
          }
          .resource-page-root .tpl-card .card-img {
            aspect-ratio: 4 / 5;
            padding: 12px;
          }
          .resource-page-root #templates {
            border-radius: 16px 16px 0 0;
            margin-top: -24px;
          }
          .resource-page-root .faq-item button {
            padding: 16px 18px;
            font-size: 15px;
          }
          .resource-page-root .faq-item .faq-answer-inner > p {
            padding: 0 18px 18px;
          }
        }
      `,
        }}
      />

      <div className='resource-page-root'>
        <div className='top-banner'>Free Shipping on All Orders · Lifetime Warranty · 30-Day Returns</div>

        <header
          className={`site-header ${scrolled ? 'scrolled' : ''}`}
          id='siteHeader'
        >
          <div className='header-inner'>
            <Link
              href='https://www.kingsyard.com'
              className='header-logo'
            >
              <KingsyardLogoMark />
            </Link>
            <nav className='header-nav'>
              <Link href='https://www.kingsyard.com/birding-guide'>BIRDING GUIDE</Link>
              <Link href='https://www.kingsyard.com/fun-facts'>FUN FACTS</Link>
              <Link
                href='/resources'
                className='current'
              >
                RESOURCE HUB
              </Link>
              <Link href='https://www.kingsyard.com/blog'>BIRDING BLOG</Link>
            </nav>
            <Link
              href='https://www.kingsyard.com/bird-feeders'
              className='header-cta'
            >
              Shop Now
            </Link>
            <button
              className='mobile-menu-btn'
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label='Menu'
            >
              <svg viewBox='0 0 24 24'>
                <line
                  x1='3'
                  y1='12'
                  x2='21'
                  y2='12'
                />
                <line
                  x1='3'
                  y1='6'
                  x2='21'
                  y2='6'
                />
                <line
                  x1='3'
                  y1='18'
                  x2='21'
                  y2='18'
                />
              </svg>
            </button>
          </div>
        </header>

        <div
          className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}
          id='mobileMenu'
        >
          <Link
            href='https://www.kingsyard.com/birding-guide'
            onClick={() => setMobileMenuOpen(false)}
          >
            BIRDING GUIDE
          </Link>
          <Link
            href='https://www.kingsyard.com/fun-facts'
            onClick={() => setMobileMenuOpen(false)}
          >
            FUN FACTS
          </Link>
          <Link
            href='/resources'
            className='current'
            onClick={() => setMobileMenuOpen(false)}
          >
            RESOURCE HUB
          </Link>
          <Link
            href='https://www.kingsyard.com/blog'
            onClick={() => setMobileMenuOpen(false)}
          >
            BIRDING BLOG
          </Link>
          <Link
            href='https://www.kingsyard.com/bird-feeders'
            className='mobile-cta'
            onClick={() => setMobileMenuOpen(false)}
          >
            Shop Now
          </Link>
        </div>

        <section className='hero'>
          <div
            className='hero-deco'
            aria-hidden='true'
          >
            <svg
              className='hb-1'
              viewBox='0 0 100 100'
              fill='none'
            >
              <path
                d='M20 70 C20 50, 35 30, 50 25 C55 23, 60 25, 58 30 C56 35, 50 38, 48 42 C46 46, 50 48, 55 46 C60 44, 65 38, 70 35 C75 32, 80 35, 78 40 C76 45, 68 50, 62 52 C56 54, 52 58, 55 62 C58 66, 65 68, 72 65 C79 62, 85 58, 90 60 C95 62, 92 70, 85 72 C78 74, 68 70, 60 68 C52 66, 45 70, 40 75 C35 80, 25 78, 20 70Z'
                stroke='#ffffff'
                strokeWidth='1.5'
              />
            </svg>
            <svg
              className='hb-2'
              viewBox='0 0 100 100'
              fill='none'
            >
              <path
                d='M20 70 C20 50, 35 30, 50 25 C55 23, 60 25, 58 30 C56 35, 50 38, 48 42 C46 46, 50 48, 55 46 C60 44, 65 38, 70 35 C75 32, 80 35, 78 40 C76 45, 68 50, 62 52 C56 54, 52 58, 55 62 C58 66, 65 68, 72 65 C79 62, 85 58, 90 60 C95 62, 92 70, 85 72 C78 74, 68 70, 60 68 C52 66, 45 70, 40 75 C35 80, 25 78, 20 70Z'
                stroke='#ffffff'
                strokeWidth='1.5'
              />
            </svg>
            <svg
              className='hb-3'
              viewBox='0 0 100 100'
              fill='none'
            >
              <path
                d='M20 70 C20 50, 35 30, 50 25 C55 23, 60 25, 58 30 C56 35, 50 38, 48 42 C46 46, 50 48, 55 46 C60 44, 65 38, 70 35 C75 32, 80 35, 78 40 C76 45, 68 50, 62 52 C56 54, 52 58, 55 62 C58 66, 65 68, 72 65 C79 62, 85 58, 90 60 C95 62, 92 70, 85 72 C78 74, 68 70, 60 68 C52 66, 45 70, 40 75 C35 80, 25 78, 20 70Z'
                stroke='#ffffff'
                strokeWidth='1.5'
              />
            </svg>
          </div>
          <span className='hero-badge'>Free Printable Resources</span>
          <h1>
            Free Printable <em>Birding Templates</em> &amp; Resources
          </h1>
          <p className='hero-sub'>
            Journals, games and care logs made for backyard birders. Fill them on screen or print them at home and head
            outside.
          </p>
          <div className='hero-badges'>
            <span>
              <svg viewBox='0 0 24 24'>
                <path d='M20 6L9 17l-5-5' />
              </svg>
              Free Templates
            </span>
            <span>
              <svg viewBox='0 0 24 24'>
                <path d='M20 6L9 17l-5-5' />
              </svg>
              New Every Season
            </span>
            <span>
              <svg viewBox='0 0 24 24'>
                <path d='M20 6L9 17l-5-5' />
              </svg>
              US Letter, Print at Home
            </span>
          </div>
        </section>

        <section
          className='section'
          id='templates'
        >
          <div className='container'>
            <div className='section-head center'>
              <span className='section-label'>The Template Library</span>
              <h2>
                Free Templates You Can <em>Print Today</em>
              </h2>
              <p>Pick a template, fill it in on screen or print it blank. Every template is free to use and share.</p>
            </div>

            <div className='tpl-grid'>
              <article className='tpl-card'>
                <div className='card-img'>
                  <img
                    src='/resources/previews/birding-journal.jpg'
                    alt='Printable birding journal template with observation log and species checklist'
                    loading='lazy'
                  />
                </div>
                <div className='card-body'>
                  <div className='card-cat'>Journals &amp; Logs</div>
                  <h2>
                    <Link href='/resources/birding-journal'>Printable Birding Journal</Link>
                  </h2>
                  <p className='card-desc'>
                    Track species, behaviors and observations across three printable field journal pages.
                  </p>
                  <div className='card-meta'>
                    <span>3 Pages</span>
                    <span>US Letter</span>
                    <span>Free</span>
                  </div>
                  <Link
                    className='card-cta'
                    href='/resources/birding-journal'
                  >
                    Open Template
                  </Link>
                </div>
              </article>

              <article className='tpl-card'>
                <div className='card-img'>
                  <img
                    src='/resources/previews/backyard-bird-bingo.jpg'
                    alt='Printable backyard bird bingo card with 24 bird species to spot'
                    loading='lazy'
                  />
                </div>
                <div className='card-body'>
                  <div className='card-cat'>Games &amp; Activities</div>
                  <h2>
                    <Link href='/resources/backyard-bird-bingo'>Printable Backyard Bird Bingo</Link>
                  </h2>
                  <p className='card-desc'>
                    Three card variants with 24 backyard birds each. Race family and friends to five in a row.
                  </p>
                  <div className='card-meta'>
                    <span>3 Pages</span>
                    <span>US Letter</span>
                    <span>Free</span>
                  </div>
                  <Link
                    className='card-cta'
                    href='/resources/backyard-bird-bingo'
                  >
                    Open Template
                  </Link>
                </div>
              </article>

              <article className='tpl-card'>
                <div className='card-img'>
                  <img
                    src='/resources/previews/feeder-care-log.jpg'
                    alt='Printable feeder care log with cleaning checklist and refill log'
                    loading='lazy'
                  />
                </div>
                <div className='card-body'>
                  <div className='card-cat'>Checklists &amp; Trackers</div>
                  <h2>
                    <Link href='/resources/feeder-care-log'>Printable Feeder Care Log</Link>
                  </h2>
                  <p className='card-desc'>
                    Keep every feeder clean, filled and safe with a simple monthly care log and cleaning checklist.
                  </p>
                  <div className='card-meta'>
                    <span>1 Page</span>
                    <span>US Letter</span>
                    <span>Free</span>
                  </div>
                  <Link
                    className='card-cta'
                    href='/resources/feeder-care-log'
                  >
                    Open Template
                  </Link>
                </div>
              </article>
            </div>

            <p className='grid-hint'>New templates are added every season.</p>
          </div>
        </section>

        <section
          className='section section-gray'
          id='how-to-use'
        >
          <div className='container'>
            <div className='section-head center'>
              <span className='section-label'>How to Use</span>
              <h2>
                Fill on Screen or <em>Print and Go</em>
              </h2>
              <p>Two ways to use every template. Pick whichever fits your day.</p>
            </div>
            <div className='howto-grid'>
              <div className='howto-card'>
                <div className='howto-num'>1</div>
                <div>
                  <h3>Fill and Print</h3>
                  <p>
                    Fill in the template right in your browser, then click Print This Page to print your completed
                    version.
                  </p>
                </div>
              </div>
              <div className='howto-card'>
                <div className='howto-num'>2</div>
                <div>
                  <h3>Print and Go</h3>
                  <p>
                    Print blank pages first, carry them outdoors, and fill them in by hand while you watch the birds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className='section'
          id='faq'
        >
          <div className='container'>
            <div className='section-head center'>
              <span className='section-label'>FAQ</span>
              <h2>Frequently Asked Questions</h2>
            </div>
            <div className='faq-wrap'>
              {[
                {
                  q: 'Are these birding templates really free?',
                  a: 'Yes. Every template in the Resource Hub is free to use, print and share. No signup and no email required.',
                },
                {
                  q: 'What paper size do the templates use?',
                  a: 'All templates are laid out for US Letter paper (8.5 x 11 in). They also print well on A4 with the standard fit to page setting.',
                },
                {
                  q: 'Can I fill in the templates on screen?',
                  a: 'Yes. Open a template and fill in the fields right in your browser, then hit Print This Page to print your completed version. You can also print blank pages first and fill them in by hand outdoors.',
                },
                {
                  q: 'Can I use these templates in my classroom or birding club?',
                  a: 'Absolutely. Teachers, scout leaders and birding clubs are welcome to print as many copies as they need for their group.',
                },
                {
                  q: 'Will you add more printable templates?',
                  a: 'Yes. New templates are added every season, from bird count sheets to seasonal checklists. Bookmark this page and check back.',
                },
              ].map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    className={`faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type='button'
                      onClick={() => toggleFaq(idx)}
                    >
                      {faq.q}
                      <span className='faq-icon'>+</span>
                    </button>
                    <div className='faq-answer'>
                      <div className='faq-answer-inner'>
                        <p>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section
          className='section section-gray'
          id='use-cases'
        >
          <div className='container'>
            <div className='section-head center'>
              <span className='section-label'>More Ways to Use Them</span>
              <h2>
                Made for Real <em>Birding Days</em>
              </h2>
            </div>
            <div className='use-grid'>
              <div className='use-card'>
                <h3>Family Birding Days</h3>
                <p>Print a few bingo cards and turn a morning at the feeder into a game everyone can play.</p>
                <Link href='https://www.kingsyard.com/blog'>Read the blog &rarr;</Link>
              </div>
              <div className='use-card'>
                <h3>New to Bird Feeding</h3>
                <p>Pair your first journal with the free bird feeding guide and get your yard set up the right way.</p>
                <Link href='https://www.kingsyard.com/welcome'>Get the free guide &rarr;</Link>
              </div>
              <div className='use-card'>
                <h3>Feeder Watching</h3>
                <p>Use the care log alongside a feeder built for wild birds and keep every visit clean and safe.</p>
                <Link href='https://www.kingsyard.com/bird-feeders'>Shop bird feeders &rarr;</Link>
              </div>
            </div>
          </div>
        </section>

        <section className='final-cta'>
          <div className='container'>
            <h2>
              Ready to Attract <em>More Birds</em>?
            </h2>
            <p className='sub'>Pair your new templates with a feeder built for wild birds and enjoy the show.</p>
            <Link
              href='https://www.kingsyard.com/bird-feeders'
              className='cta-btn'
            >
              Shop Bird Feeders
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M5 12h14' />
                <path d='M13 6l6 6-6 6' />
              </svg>
            </Link>
          </div>
        </section>

        <footer>
          <div className='footer-copyright'>
            &copy; <span>{new Date().getFullYear()}</span> Kingsyard Inc.
          </div>
          <ul className='footer-links'>
            <li>
              <Link href='https://www.kingsyard.com/privacy-policy'>Privacy Policy</Link>
            </li>
            <li>
              <Link href='https://www.kingsyard.com/terms-privacy'>Terms of Use</Link>
            </li>
            <li>
              <Link href='https://www.kingsyard.com/contact-us'>Contact Us</Link>
            </li>
            <li>
              <Link href='https://www.kingsyard.com/bird-feeders'>Shop</Link>
            </li>
          </ul>
        </footer>
      </div>
    </>
  );
}
